/**
 * Query parsing (PLAN §2.2, §3.1). Pure.
 *
 * A prefix is a word at the very start of the query followed by `:` (`boards:`,
 * `b:x`, `boards: x`), or a registered one-character sigil (`>`, `@`, `#`, `?`).
 * Keywords are English syntax constants in every language (chat-search D21);
 * matching is case-insensitive; an unknown word before `:` (`https://…`,
 * `foo:`) stays plain text. Terms come from the chat-search tokenizer, so
 * quoting behaves the same ("quoted phrase" is one term).
 */
import { resolvePrefix } from './registry';
import { tokenizeQuery } from '../utils/chatSearchHighlight';

const PREFIX_RE = /^([A-Za-z][A-Za-z0-9-]*):([\s\S]*)$/;

/**
 * A registered prefix at the start of `input`, or null.
 * @returns {{ providerId: string, word: string, rest: string } | null}
 */
export function detectPrefix(input, resolve = resolvePrefix) {
  const s = String(input ?? '');
  if (!s) return null;
  const first = s[0];
  if (!/[A-Za-z0-9\s]/.test(first)) {
    const p = resolve(first);
    if (p) return { providerId: p.id, word: first, rest: s.slice(1).replace(/^\s+/, '') };
    return null;
  }
  const m = PREFIX_RE.exec(s);
  if (!m) return null;
  // `https://x`, `c:\path`: a colon followed by a slash is never a prefix.
  if (/^[\\/]/.test(m[2])) return null;
  const p = resolve(m[1]);
  if (!p) return null;
  return { providerId: p.id, word: m[1].toLowerCase(), rest: m[2].replace(/^\s+/, '') };
}

/**
 * @param {string} input   what is in the input box
 * @param {{ chip?: string|null, resolve?: Function }} [opts]  the active chip's provider id
 * @returns {{ scope: string|null, scopeFrom: 'prefix'|'chip'|null, text: string, terms: string[], filters: object, tooLong: boolean }}
 */
export function parseQuery(input, { chip = null, resolve = resolvePrefix } = {}) {
  let scope = null;
  let scopeFrom = null;
  let text = String(input ?? '');
  if (chip) {
    scope = chip;
    scopeFrom = 'chip';
  } else {
    const hit = detectPrefix(text, resolve);
    if (hit) {
      scope = hit.providerId;
      scopeFrom = 'prefix';
      text = hit.rest;
    }
  }
  text = text.trim();
  const tok = tokenizeQuery(text);
  return { scope, scopeFrom, text, terms: tok.terms || [], filters: {}, tooLong: !!tok.error };
}
